// @ts-nocheck
import React, { useCallback, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EFieldType, TBundleFieldType, IBundlePromotionForm } from "store/reducers/bundlePromotion.reducers";
import { setBundlePromotionObject } from "store/actions/bundlePromotion.actions";
import { EPromotionTypes, IState } from "store/store.interface";
import { regEx } from "utils/app.utils";
import RadioGroup from "components/RadioGroup";
import DateRangeWithTiming from "../../PromotionForm/CustomFields/DateRangeWithTiming";
import { Box, Input, Select, } from "common";
import CustomFileUpload from './CustomFileUpload'

interface IProps {
  disable?: boolean;
  formCode?: string;
  field: TBundleFieldType;
  registerField: (field: any) => void;
  unRegisterField: (name: string) => void;
  onValidateInput: (error: any) => void;
  getValue: (fieldName: string) => any;
  error?: boolean;
  errorMessage?: string;
  testId?: string;
  variant?: string;
}

const RenderFields: React.FC<IProps> = ({
  disable,
  formCode,
  field,
  onValidateInput,
  getValue,
  error,
  errorMessage,
  testId,
  registerField,
  unRegisterField,
  variant,
}) => {
  const dispatch = useDispatch();

  const {
    selectOptions,
    creationBundleForm,
  } = useSelector((state: IState) => ({
    ...state.config,
    ...state.auth,
    ...state.bundlePromotion,
  }));

  useEffect(() => {
    if (getValue(field.name) === "BXGY" && getValue("discountValue")) {
      dispatch(setBundlePromotionObject('', "discountValue"));
    }
  })

  /**
   * validate inputs and set errors
   */
  const validateInputs = useCallback(
    (value: any, field: TBundleFieldType) => {
      const {
        validator,
        maxChar = 1000,
        minChar = 0,
        maxNumber = Number.MAX_VALUE,
        minNumber = 1,
        name,
        errorMessage,
      } = field;
      const limitRegex = new RegExp(`^.{${minChar},${maxChar}}$`);
      let validationRule;
      let isError = false;
      let message = "";
      if (!limitRegex.test(value)) {
        isError = true;
        message = errorMessage || 'Maximum 25 characters allowed';
      }
      else if (
        !isNaN(value) &&
        validator === "number" &&
        (+value < minNumber || +value > maxNumber)
      ) {
        isError = true;
        message =
          maxNumber === Number.MAX_VALUE
            ? `Number should be more than or equal to ${minNumber}`
            : `Number should be between ${minNumber} and ${maxNumber}`;
      }
      else if (typeof validator === "string") {
        validationRule = regEx[validator];
      }
      if (validationRule && !validationRule.test(value)) {
        message = errorMessage || `Input value is not valid`;
        isError = true;
      }
      return { [name]: { isError, message } };
    },
    []
  );

  /**
   * on Register Field
   */
  useEffect(() => {
    if(creationBundleForm?.type !== EPromotionTypes.FREE_GIFT){
      dispatch(setBundlePromotionObject('1', 'discountQuantity'));
    }
    registerField(field);
    const fieldValue = getValue(field.name);

    if (fieldValue) {
      onValidateInput(validateInputs(fieldValue, field));
    }
    return () => {
      onValidateInput({ [field.name]: { isError: false } });
      unRegisterField(field.id);
    };
  }, []);

  const onChangeInputHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {

      onValidateInput(validateInputs(event.target.value.trim(), field));
      dispatch(setBundlePromotionObject(event.target.value, field.name));
    },
    [dispatch, field, onValidateInput, validateInputs]
  );

  const onInputBlur = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setBundlePromotionObject(event.target.value.trim(), field.name));
    },
    [dispatch, field]
  );

  const onChangeDateHandler = useCallback(
    ({
      startDate,
      endDate,
    }: {
      startDate: Date | null;
      endDate: Date | null;
    }) => {
      dispatch(setBundlePromotionObject(startDate, field.startDateKeys));
      dispatch(setBundlePromotionObject(endDate, field.endDateKeys));
    },
    [dispatch, field]
  );


  const onChangeRadioHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setBundlePromotionObject(event.target.value, field.name));
    },
    [dispatch, field]
  );

  const onChangeSelectHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setBundlePromotionObject(event.target.value, field.name));
    },
    [dispatch, field]
  );

  const onCompleteFileUpload = useCallback(
    (name: string) => (result: ParseResult<any>) => {
      let finalSkus = ([] as string[]).concat(...result);
      dispatch(setBundlePromotionObject(finalSkus, name));
    },
    [dispatch]
  );

  const {
    name,
    type,
    optionsType,
    id,
  } = useMemo(() => field, [field]);

  const options = useMemo(() => {
    let options = selectOptions[optionsType || ""] || [];
    return options;
  }, [selectOptions, optionsType]);

  const makeFieldDisableOnEdit = useMemo(() => {
    if (!field.isEditable && creationBundleForm.edit) {
      return true;
    } else if (field.disabled) {
      return true;
    } else {
      return false;
    }
  }, [creationBundleForm, field]);
  
  // This is used to disable file upload field when includedPosId or excludedPosId is selected
  const makeFileUploadDisabled = (
    field,
    creationBundleForm: IBundlePromotionForm,
    disable?: boolean,
  ) => {
    const hasPos = !!creationBundleForm["includedPosId"];
    const hasExPos = !!creationBundleForm["excludedPosId"];

    if (field.name === "includedPosId") {
      return hasExPos;
    }
    if (field.name === "excludedPosId") {
      return hasPos;
    }
    return disable;
  };

  const renderField = () => {

    switch (type) {
      case EFieldType.input:
        return (
          <Input
            disabled={makeFieldDisableOnEdit}
            name={name}
            id={id}
            value={getValue(name)}
            defaultValue={getValue(name)}
            onChange={onChangeInputHandler}
            onBlur={onInputBlur}
            fontSize="16px"
            testId={`${field.type}-${field.id}-field-${testId}`}
            size="medium"
            error={error}
            errorMessage={errorMessage}
            multiline={field.multiline}
            variant={variant}
          />
        );
      case EFieldType.select:
        return (
          <Select
            disable={(makeFieldDisableOnEdit || disable)}
            id={id}
            testId={`${field.type}-${field.id}-field-${testId}`}
            name={name}
            options={options}
            nativeValue={getValue(name) ?? ''}
            onNativeChange={onChangeSelectHandler}
            size="medium"
            disableUnderline={false}
            variant="native"
            width="100%"
            error={error}
            errorMessage={errorMessage}
            inputVariant='outlined'
          />
        );
      case EFieldType.dateRange:
        return (
          <DateRangeWithTiming
            disable={disable}
            isEdit={creationBundleForm.edit}
            testId={`${field.type}-${field.id}-field-${testId}`}
            field={field}
            fieldError={error}
            getValue={getValue}
            onValidateInput={onValidateInput}
            onChange={onChangeDateHandler}
            gap="40px"
            maxYearsAllowed={field.maxYears}
          />
        );
      case EFieldType.radioGroup:
        return (
          <RadioGroup
            disabled={(makeFieldDisableOnEdit || disable)}
            testId={`${field.type}-${field.id}-field-${testId}`}
            value={getValue(name)}
            onChange={onChangeRadioHandler}
            options={options}
          />
        );
      case EFieldType.file:
        return (
          <CustomFileUpload
            name={field.name}
            disable={makeFileUploadDisabled(field, creationBundleForm, disable)}
            onCompleteUpload={onCompleteFileUpload(field.name)}
            fileSelected={!!(getValue(field.name)?.length)}
            onValidateInput={onValidateInput}
            uploadStrategy={field.uploadStrategy}
          />
        );
      default:
        return null;
    }
  }

  return (
    <Box display="flex">{renderField()}</Box>
  );
};

export default RenderFields;
